
import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { useIsMobile } from "@/hooks/use-mobile";
import { CheckCircle, CreditCard, Clock, FileCheck } from "lucide-react";
import IntellendLogo from "@/components/IntellendLogo";
import { cookieExistsAndValid } from '@/utils/cookies';
import { useState, useEffect } from "react";
import { useLoader } from "../contexts/LoaderContext";
import { API_BASE_URL } from '../config';
import axios from "../lib/axios";

const Home = () => {
  const { enc_id } = useParams<{ enc_id: string }>();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [visibleLogin, setVisibleLogin] = useState(false);
  const { loading, setLoading } = useLoader();

  const checkBapIdentifier = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API_BASE_URL}/api/check_bap_identifier_active_session/`, {
        withCredentials: true, // send HttpOnly cookie
      });
      setVisibleLogin(res.data.valid); // show login if session valid
    } catch (err) {
      setVisibleLogin(false);
      if (err.response) {
        console.log("Invalid cookie, status:", err.response.status);
        console.log(err.response.data.detail);
      } else {
        console.error("Network error or no response");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (enc_id) {
      if (enc_id === 'WHITESPACE') {
        const mode = import.meta.env.MODE;
        const allowedModes = ['development', 'register'];
        if (!allowedModes.includes(mode)) {
          navigate('/404', { replace: true });
          return;
        }
      }
      // enc_id present → skip bap_identifier check
      setVisibleLogin(true);
      return;
    }

    checkBapIdentifier();
  }, [enc_id]);

  const handleGetStarted = () => {
    if (enc_id) {
      navigate(`/consent2/${enc_id}`, {
        state: { from: 'home' }
      });

      return;
    }
    navigate('/index');
  };

  return (
    <div className="min-h-screen bg-intellend-white">
      {/* Header */}
      <header className="bg-intellend-green p-4">
        <div className="container mx-auto flex justify-between items-center">
          <div className="bg-intellend-white px-2 py-1 rounded-md">
            <IntellendLogo size={isMobile ? "sm" : "md"} />
          </div>
          {visibleLogin &&
            <Button
              variant="outline"
              className="bg-intellend-white text-intellend-green hover:bg-intellend-white/90"
              onClick={handleGetStarted}
            >
              Login
            </Button>
          }
        </div>
      </header>

      {/* Hero section - more compact for mobile */}
      <section className="bg-intellend-green text-intellend-white py-8 md:py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-2xl md:text-5xl font-bold mb-4">
              Financial Solutions for Indian MSMEs
            </h1>
            <p className="text-sm md:text-xl mb-6 opacity-90">
              Access business loans with transparent terms and competitive rates
            </p>
            {visibleLogin &&
              <Button
                className="bg-intellend-gold hover:bg-intellend-gold/90 text-intellend-white px-6 py-2 md:px-8 md:py-6 md:h-auto"
                onClick={handleGetStarted}
              >
                Get Started
              </Button>
            }
          </div>
        </div>
      </section>

      {/* Features section - compact grid for mobile */}
      <section className="py-8 md:py-16 bg-intellend-white">
        <div className="container mx-auto px-4">
          <h2 className="text-xl md:text-3xl font-bold text-center text-intellend-green mb-6 md:mb-12">
            Why Choose Intellend?
          </h2>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-8 max-w-6xl mx-auto">
            <div className="bg-white p-3 md:p-6 rounded-lg shadow-md flex flex-col items-center text-center">
              <div className="bg-intellend-green/10 p-2 md:p-4 rounded-full mb-2 md:mb-4">
                <CreditCard className="text-intellend-green h-5 w-5 md:h-8 md:w-8" />
              </div>
              <h3 className="text-sm md:text-xl font-semibold mb-1 md:mb-2 text-intellend-green">Transparent Fee</h3>
              {!isMobile && (
                <p className="text-gray-600">Know exactly what you'll pay upfront with our simple fee structure.</p>
              )}
            </div>

            <div className="bg-white p-3 md:p-6 rounded-lg shadow-md flex flex-col items-center text-center">
              <div className="bg-intellend-green/10 p-2 md:p-4 rounded-full mb-2 md:mb-4">
                <Clock className="text-intellend-green h-5 w-5 md:h-8 md:w-8" />
              </div>
              <h3 className="text-sm md:text-xl font-semibold mb-1 md:mb-2 text-intellend-green">Daily Repayment</h3>
              {!isMobile && (
                <p className="text-gray-600">Automatic daily repayments make budgeting simple for your business.</p>
              )}
            </div>

            <div className="bg-white p-3 md:p-6 rounded-lg shadow-md flex flex-col items-center text-center">
              <div className="bg-intellend-green/10 p-2 md:p-4 rounded-full mb-2 md:mb-4">
                <FileCheck className="text-intellend-green h-5 w-5 md:h-8 md:w-8" />
              </div>
              <h3 className="text-sm md:text-xl font-semibold mb-1 md:mb-2 text-intellend-green">Minimal Docs</h3>
              {!isMobile && (
                <p className="text-gray-600">Our hassle-free application requires minimal documentation.</p>
              )}
            </div>

            <div className="bg-white p-3 md:p-6 rounded-lg shadow-md flex flex-col items-center text-center">
              <div className="bg-intellend-green/10 p-2 md:p-4 rounded-full mb-2 md:mb-4">
                <CheckCircle className="text-intellend-green h-5 w-5 md:h-8 md:w-8" />
              </div>
              <h3 className="text-sm md:text-xl font-semibold mb-1 md:mb-2 text-intellend-green">Fast Processing</h3>
              {!isMobile && (
                <p className="text-gray-600">Quick decisions get funds in your account when you need them.</p>
              )}
            </div>
          </div>

          <div className="text-center mt-6 md:mt-12">
            {visibleLogin &&
              <Button
                className="bg-intellend-green hover:bg-intellend-green/90"
                onClick={handleGetStarted}
              >
                Apply Now
              </Button>
            }
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-50 py-4 md:py-8 border-t">
        <div className="container mx-auto px-4 text-center">
          <p className="text-gray-600 text-xs md:text-sm hidden">
            2026 Intellend. All rights reserved.
          </p>
          <a
            href="/experian-consent-revocation"
            className="text-intellend-green hover:text-intellend-green/80 text-sm font-medium mt-2 inline-block"
          >
            Revoke Experian Consent
          </a>
        </div>
      </footer>
    </div>
  );
};

export default Home;
